export interface Theme {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
}

// Re-curagem de contraste (jun/2026): primaries de 5 temas trocados pro tom
// "1 step mais escuro" da mesma família Material, atingindo contraste WCAG
// AA (≥4.5) com branco. Secondary/accent ficam — são decorativos. Mudanças:
//   Vermelho       #E53935 → #DC2626 (4.23 → 4.83)  ← alinha com schema default
//   Pink           #FF1744 → #C2185B (3.93 → 5.87)
//   Azul           #1E88E5 → #1565C0 (4.11 → 5.75)
//   Turquesa       #00897B → #00695C (4.32 → 6.61)
//   Laranja Escuro #F4511E → #BF360C (4.35 → 5.60)
// Lojas que JÁ ESTAVAM em algum desses hexs antigos são migradas via
// scripts/migrate-theme-contrast.sql — sem isso a UI só ajuda quem
// (re)selecionar o tema dali pra frente.
export const THEMES: Theme[] = [
  { name: "Vermelho", primary: "#DC2626", secondary: "#EF5350", accent: "#B71C1C" },
  { name: "Pink", primary: "#C2185B", secondary: "#FF4081", accent: "#C51162" },
  { name: "Rosa", primary: "#D81B60", secondary: "#EC407A", accent: "#880E4F" },
  { name: "Rosa Claro", primary: "#F48FB1", secondary: "#F8BBD0", accent: "#EC407A" },
  { name: "Roxo", primary: "#8E24AA", secondary: "#AB47BC", accent: "#4A148C" },
  { name: "Roxo Escuro", primary: "#5E35B1", secondary: "#7E57C2", accent: "#311B92" },
  { name: "Índigo", primary: "#3949AB", secondary: "#5C6BC0", accent: "#1A237E" },
  { name: "Azul", primary: "#1565C0", secondary: "#42A5F5", accent: "#0D47A1" },
  { name: "Azul Claro", primary: "#039BE5", secondary: "#29B6F6", accent: "#01579B" },
  { name: "Ciano", primary: "#00ACC1", secondary: "#26C6DA", accent: "#006064" },
  { name: "Turquesa", primary: "#00695C", secondary: "#26A69A", accent: "#004D40" },
  { name: "Verde", primary: "#43A047", secondary: "#66BB6A", accent: "#1B5E20" },
  { name: "Verde Claro", primary: "#7CB342", secondary: "#9CCC65", accent: "#33691E" },
  { name: "Lima", primary: "#C0CA33", secondary: "#D4E157", accent: "#827717" },
  { name: "Amarelo", primary: "#FDD835", secondary: "#FFEE58", accent: "#F57F17" },
  { name: "Âmbar", primary: "#FFB300", secondary: "#FFCA28", accent: "#FF6F00" },
  { name: "Laranja", primary: "#FB8C00", secondary: "#FFA726", accent: "#E65100" },
  { name: "Laranja Escuro", primary: "#BF360C", secondary: "#FF7043", accent: "#BF360C" },
  { name: "Marrom", primary: "#6D4C41", secondary: "#8D6E63", accent: "#3E2723" },
  { name: "Preto e Dourado", primary: "#212121", secondary: "#C9A227", accent: "#8B6F1F" },
  { name: "Cinza", primary: "#757575", secondary: "#9E9E9E", accent: "#212121" },
  { name: "Azul Cinzento", primary: "#546E7A", secondary: "#78909C", accent: "#263238" },
];

export const DEFAULT_THEME_NAME = "Vermelho";
export const CUSTOM_THEME_NAME = "Personalizado";

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return null;
  return [parseInt(m[1]!, 16), parseInt(m[2]!, 16), parseInt(m[3]!, 16)];
}

function colorDistance(a: string, b: string): number {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return Infinity;
  const dr = ra[0] - rb[0];
  const dg = ra[1] - rb[1];
  const db = ra[2] - rb[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

export function getThemeByName(name: string | null | undefined): Theme | null {
  if (!name) return null;
  return THEMES.find((t) => t.name === name) ?? null;
}

/**
 * Returns the theme whose primary color is closest to the given hex.
 * If the closest distance exceeds `threshold` (default 50), returns null
 * (meaning the store has a custom color that doesn't match any theme).
 */
export function findClosestTheme(
  primary: string | null | undefined,
  threshold = 50,
): Theme | null {
  if (!primary) return null;
  let best: Theme | null = null;
  let bestDist = Infinity;
  for (const t of THEMES) {
    const d = colorDistance(primary, t.primary);
    if (d < bestDist) {
      bestDist = d;
      best = t;
    }
  }
  if (bestDist > threshold) return null;
  return best;
}
